import * as Sentry from '@sentry/nextjs'

import { WEBSITE_URL } from '@/constants/common'

// Headless chrome and Crawler bots are by default blocked by Moengage
export const isMoengageBlockedUserAgent = () =>
  typeof window !== 'undefined' &&
  /bot|headless|crawler|spider|crawling/i.test(navigator?.userAgent)

export const shouldInitializeMoengage = () => !isMoengageBlockedUserAgent()

const MOENGAGE_INIT_FILE = '/moengage-init.js'

/** Use origin only — APP_URL is sometimes set with a locale path (e.g. https://dehaat.in/en). */
const resolveMoengageInitOrigin = (): string | undefined => {
  const raw = process.env.NEXT_PUBLIC_APP_URL?.trim()
  if (!raw) return undefined

  try {
    return new URL(raw).origin
  } catch {
    return undefined
  }
}

/** Root-absolute URL — locale-prefixed paths like /en/moengage-init.js 404 on static public files. */
export const getMoengageInitScriptSrc = () => {
  const origin = resolveMoengageInitOrigin()
  if (origin) {
    return `${origin}${MOENGAGE_INIT_FILE}`
  }
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'production') {
    return `${WEBSITE_URL}${MOENGAGE_INIT_FILE}`
  }
  return MOENGAGE_INIT_FILE
}

export const isMoengageIntialized = () =>
  typeof window !== 'undefined' && 'Moengage' in window

export const normalizeMoengagePageLocation = (url: string): string => {
  if (typeof window === 'undefined') return url
  return /^https?:\/\//i.test(url)
    ? url
    : `${window.location.origin}${url}`
}

/** Call synchronously before page unload — do not queue (logout/navigation). */
export function destroyMoengageSession() {
  if (typeof window === 'undefined' || !shouldInitializeMoengage()) return

  if (
    window.Moengage &&
    typeof window.Moengage.destroy_session === 'function'
  ) {
    try {
      Moengage.destroy_session()
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

const MOENGAGE_READY_MAX_RETRIES = 100
const MOENGAGE_POLL_INTERVAL_MS = 300

function getMoengageInitDiagnostics() {
  if (typeof window === 'undefined') {
    return { moengageType: 'ssr' }
  }

  const moe = window.Moengage as
    | { initialised?: number; invoked?: number; track_event?: unknown }
    | undefined

  return {
    moengageType:
      moe == null ? 'null' : Array.isArray(moe) ? 'array' : typeof moe,
    hasTrackEvent: typeof moe?.track_event === 'function',
    initialised: moe?.initialised,
    invoked: moe?.invoked,
  }
}

type QueuedMoengageEvent = {
  name: string
  params: Record<string, string | number | boolean>
}

let eventQueue: QueuedMoengageEvent[] = []
let callbackQueue: Array<() => void> = []
let flushScheduled = false
let flushTimedOutReported = false

function isValidMoengageClient(
  moe: unknown,
): moe is { track_event: (name: string, params: Record<string, unknown>) => void } {
  return (
    moe != null &&
    !Array.isArray(moe) &&
    typeof moe === 'object' &&
    typeof (moe as { track_event?: unknown }).track_event === 'function'
  )
}

function isMoengageTrackReady() {
  return typeof window !== 'undefined' && isValidMoengageClient(window.Moengage)
}

function isMoengageUserReady() {
  return (
    isMoengageTrackReady() &&
    typeof window.Moengage.add_user_attribute === 'function'
  )
}

function isMoengageSdkReady() {
  return isMoengageUserReady()
}

function scheduleMoengageFlush() {
  if (!flushScheduled) {
    flushScheduled = true
    setTimeout(() => flushMoengageQueues(0), MOENGAGE_POLL_INTERVAL_MS)
  }
}

function flushMoengageQueues(retryCount = 0) {
  flushScheduled = false

  if (isMoengageTrackReady()) {
    while (eventQueue.length > 0) {
      const { name, params } = eventQueue.shift()!
      try {
        Moengage.track_event(name, params)
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  }

  if (isMoengageUserReady()) {
    while (callbackQueue.length > 0) {
      const callback = callbackQueue.shift()!
      try {
        callback()
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  }

  const hasPendingWork =
    eventQueue.length > 0 || callbackQueue.length > 0
  const canFlushAll =
    (!eventQueue.length || isMoengageTrackReady()) &&
    (!callbackQueue.length || isMoengageUserReady())

  if (canFlushAll && !hasPendingWork) {
    return
  }

  if (retryCount >= MOENGAGE_READY_MAX_RETRIES) {
    if (!flushTimedOutReported) {
      flushTimedOutReported = true
      const droppedEvents = eventQueue.length
      const droppedCallbacks = callbackQueue.length
      eventQueue = []
      callbackQueue = []
      if (droppedEvents > 0 || droppedCallbacks > 0) {
        Sentry.captureMessage(
          `MoEngage SDK did not become ready; dropped ${droppedEvents} events and ${droppedCallbacks} callbacks`,
          {
            level: 'warning',
            extra: getMoengageInitDiagnostics(),
          },
        )
      }
    }
    return
  }

  flushScheduled = true
  setTimeout(
    () => flushMoengageQueues(retryCount + 1),
    MOENGAGE_POLL_INTERVAL_MS,
  )
}

export function trackMoengageEvent(
  name: string,
  params: Record<string, string | number | boolean>,
) {
  if (typeof window === 'undefined' || !shouldInitializeMoengage()) return

  if (isMoengageTrackReady()) {
    try {
      Moengage.track_event(name, params)
    } catch (error) {
      Sentry.captureException(error)
    }
    return
  }

  eventQueue.push({ name, params })
  scheduleMoengageFlush()
}

/** Queue SDK actions (user attributes, add_mobile, destroy_session) until ready. */
export function runWhenMoengageReady(callback: () => void) {
  if (typeof window === 'undefined' || !shouldInitializeMoengage()) return

  if (isMoengageSdkReady()) {
    try {
      callback()
    } catch (error) {
      Sentry.captureException(error)
    }
    return
  }

  callbackQueue.push(callback)
  scheduleMoengageFlush()
}

/** @deprecated Use runWhenMoengageReady — kept for existing imports. */
export const waitForMoengageReady = runWhenMoengageReady

export const buildMoengageEventParams = (
  baseParams: Record<string, unknown>,
  extraParams: Record<string, unknown> = {},
): Record<string, string | number | boolean> => {
  const merged = { ...baseParams, ...extraParams }
  delete merged.event_params

  const result: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(merged)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'object') {
      result[key] = JSON.stringify(value)
    } else {
      result[key] = value as string | number | boolean
    }
  }
  return result
}

/** @internal Resets module queues — for unit tests only. */
export const resetMoengageQueuesForTests = () => {
  eventQueue = []
  callbackQueue = []
  flushScheduled = false
  flushTimedOutReported = false
}

if (typeof window !== 'undefined') {
  window.addEventListener('moengage:configured', () => {
    flushMoengageQueues(0)
  })

  window.addEventListener('moengage:init-failed', () => {
    Sentry.captureMessage('MoEngage CDN SDK failed to load', {
      level: 'error',
      extra: getMoengageInitDiagnostics(),
    })
  })
}
